import { Injectable, inject } from "@angular/core";
import moment from "moment";
import { Observable, firstValueFrom } from "rxjs";
import { MaskinlagtabInformationDTO } from "~app/common/models/arbetsorder/maskinlag/MaskinlagtabInformationDTO";
import { Anskaffningsformer, Operationstyper, ValsSynkroniseringsstatusTyp, VirkesorderStatus } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CalendarService } from "~app/common/services/calendar.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { IMaskinlagForArbetsorderResponse } from "~app/features/planera/mina-maskinlag/mina-maskinlag.models";
import { IOperation } from "~app/features/planera/model/IOperation";
import { ArbetsorderPlaneringSchedulerTexts } from "../arbetsorder-planering-scheduler.texts";
import { DateErrorType } from "../models/DateErrorType";
import { IMaskinLagSchedulerEvent } from "../models/IMaskinLagSchedulerEvent";
import { SchemaPopupInfoDTO } from "../models/SchemaPopupInfoDTO";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderPlaneringSchedulerService {
    private readonly apiClientService = inject(ApiClientService);
    private readonly webinstallningarService = inject(WebinstallningarService);
    private readonly calendarService = inject(CalendarService);

    private readonly WIK = "arbetsorderplanering.scheduler.zoom";
    private readonly url: string;
    private readonly texts = ArbetsorderPlaneringSchedulerTexts.texts;

    private readonly cssClassDefaultEvent = "event-default";
    private readonly cssClassSkordningPaborjadEvent = "event-skordning-paborjad";
    private readonly cssClassSkotningAvslutadEvent = "event-skotning-avslutad";
    private readonly cssClassActiveEvent = " event-active";

    constructor() {
        this.url = "Planera";
    }

    getMinaMaskinlagAndOperationer(shortDate: string): Observable<IMaskinlagForArbetsorderResponse> {
        return this.apiClientService.get<IMaskinlagForArbetsorderResponse>(`${this.url}/GetMinaMaskinlagAndOperationer`, true, {
            startdatum: shortDate
        });
    }

    getMaskinlagForArbetsorder(arbetsorderId: number, shortDate?: string): Observable<IMaskinlagForArbetsorderResponse> {
        const params: any = {
            arbetsorderId: arbetsorderId
        };

        if (shortDate) {
            params.startdatum = shortDate;
        }

        return this.apiClientService.get<IMaskinlagForArbetsorderResponse>(`${this.url}/GetMaskinlagForArbetsorder`, true, params);
    }

    async getZoom(): Promise<number> {
        const settings = await this.webinstallningarService.getAsync<ISettings>(this.WIK);

        if (settings) {
            return settings.zoom;
        }

        return undefined;
    }

    async setZoom(value: number): Promise<void> {
        await this.webinstallningarService.setAsync<ISettings>(this.WIK, { zoom: value });
    }

    getClassNamesForOperation(o: IOperation, active = false): string {
        let classNames: string;
        if (o.skotningAvslutadDatum) {
            classNames = this.cssClassSkotningAvslutadEvent;
        } else if (o.skordningPaborjadDatum) {
            classNames = this.cssClassSkordningPaborjadEvent;
        } else {
            classNames = this.cssClassDefaultEvent;
        }

        if (active) {
            classNames += this.cssClassActiveEvent;
        }

        return classNames;
    }

    buildHtmlForMoveError = (dateError: DateErrorType) => {
        let text = "";
        // eslint-disable-next-line no-bitwise
        if (dateError & DateErrorType.Avverkningsanmalan) {
            text += this.texts.schemalaggningUtanforAvverkningsanmalan;
        }
        // eslint-disable-next-line no-bitwise
        if (dateError & DateErrorType.Certifikat) {
            text += this.texts.schemalaggningUtanforDatumForCertifikatskontroll;
        }

        return `<div><i class="far fa-sm fa-exclamation-triangle rask-danger" aria-hidden="true"></i>${text}</div>`;
    };

    buildHtmlForEvent = (event: IMaskinLagSchedulerEvent): string => {
        let tags = "";
        if (event.tags.dateError) {
            tags += `<i class="far fa-sm fa-exclamation-triangle rask-danger" aria-hidden="true"></i>`;
        }
        const virkesorderStatusIcon = `${this.virkesorderStatusIcon(event)}<br/>`;
        const affarsform = this.buildHtmlForAffarsformIkon(event);
        const valsStatusIcon = event.valsSynkroniseringsstatus === ValsSynkroniseringsstatusTyp.Fel ?
            "<i class='fas fa-xs fa-paper-plane details-icon table-cell-icon-error'></i>" : "";

        return `<div class="event-icons-wrapper">${virkesorderStatusIcon}${valsStatusIcon}${affarsform}${tags}</div>
        <div class="event-text-wrapper">
            <div class="event-text">${event.tags.namn}${this.getSkordarPrestation(event)}<br/><br/></div>
        </div>`;
    };

    virkesorderStatusIcon(event: IMaskinLagSchedulerEvent) {
        let style: { icon: string; color: string };
        if (event.arbetsorderviol3) {
            style = VirkesorderStatus.VirkesorderStatusIconStyleViol3(
                event.hasTransmissionErrorProduktionViol3,
                event.hasTransmissionErrorTransportViol3,
                event.dirtyProduktionsdel,
                event.dirtyTransportdel,
                event.skickadTillViol3);
        } else {
            style = VirkesorderStatus.VirkesorderStatusIconStyle(event.virkesorderStatus);
        }

        return `<i class="far fa-sm ${style.icon}" aria-hidden="true" style="color: ${style.color}"></i>`;
    }

    filterOperationerOnTyp(operationer: IOperation[]): IOperation[] {
        return operationer.filter(o => o.operationstyp === Operationstyper.Skordning || o.operationstyp === Operationstyper.Grotskotning);
    }

    getZoomDelta(currentZoom: number, zoomOut: boolean): number {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (zoomOut) {
            if (currentZoom <= 20) {
                return currentZoom - 1;
            } else if (currentZoom <= 50) {
                return Math.max(currentZoom - 5, 20);
            }

            return Math.max(currentZoom - 10, 50);
        } else {
            if (currentZoom < 20) {
                return currentZoom + 1;
            } else if (currentZoom < 50) {
                return currentZoom + 5;
            }

            return currentZoom + 10;
        }
    }

    getMaskinlagtabInfo(arbetsorderId: number, maskinlagId?: number): Promise<MaskinlagtabInformationDTO> {
        const params: any = { arbetsorderId: arbetsorderId };
        if (maskinlagId) {
            params.maskinlagId = maskinlagId;
        }

        return firstValueFrom(this.apiClientService.get<MaskinlagtabInformationDTO>("Planera/GetMaskinlagtabInformation", false, params));
    }

    getSchemaPopupInfo(arbetsorderId: number, maskinlagId?: number): Observable<SchemaPopupInfoDTO> {
        const params: any = { arbetsorderId: arbetsorderId };
        if (maskinlagId) {
            params.maskinlagId = maskinlagId;
        }

        return this.apiClientService.get<SchemaPopupInfoDTO>("Planera/GetSchemaPopupInfo", false, params);
    }

    readonly isWeekendOrHelgdag = (date: string | Date | moment.Moment): boolean => {
        const day = moment(date);
        return day.isoWeekday() === 7 || day.isoWeekday() === 6 || this.calendarService.isHelgdag(day);
    };

    private buildHtmlForAffarsformIkon(event: IMaskinLagSchedulerEvent): string {
        return (event.arbetsorderAffarsform && Anskaffningsformer.isVirkesKop(event.arbetsorderAnskaffningsform)) ?
            `<i class="fal fa-sm fa-handshake" aria-hidden="true"></i>` : "";
    }

    private getSkordarPrestation(event: IMaskinLagSchedulerEvent): string {
        const volym = event.volymArbetsorder;
        const antalDagar = DateUtils.countDaysOfType(moment(event.start.toString()), moment(event.end.toString()), date => !this.isWeekendOrHelgdag(date));
        const prestation = antalDagar ? volym / antalDagar : volym;

        return `<span class="scheduler-prestation">${prestation?.toFixed(0)}/dag</span>`;
    }
}

interface ISettings {
    zoom: number;
}
